import { inject, Injectable, InjectionToken } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { SharedHttpService } from '@luggagehero/shared/services/http';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { cloneDeep } from '@luggagehero/shared/util';
import { Observable, ReplaySubject } from 'rxjs';

import { AppConfig } from './app-config';
import { AppSettings } from './interfaces';
import { SharedAppSettings } from './shared-app-settings';
import { UrlSettingOverridesService } from './url-setting-overrides.service';

export const APP_SETTINGS_SERVICE_ENDPOINT_TOKEN = new InjectionToken<string>('APP_SETTINGS_SERVICE_ENDPOINT_TOKEN');

@Injectable({
  providedIn: 'root',
})
export class SharedAppSettingsService {
  private http = inject(SharedHttpService);
  private log = inject(SharedLoggingService);
  private urlOverrides = inject(UrlSettingOverridesService);
  private appSettingsServiceEndpoint = inject(APP_SETTINGS_SERVICE_ENDPOINT_TOKEN);

  /**
   * Observable of the current settings
   */
  public $current: Observable<SharedAppSettings>;

  private apiEndPoint: string;
  private settings: ReplaySubject<SharedAppSettings>;
  private _current?: SharedAppSettings;
  // The in-flight (or completed) init promise. Cached so repeated callers AWAIT
  // the same completion: a boolean guard set before the awaited fetch once let a
  // second caller read `current` (undefined) and crash on `.minAppVersion`
  // (AM-4781). Idempotent per appName: the first call does the work, later calls
  // with the same appName await its promise; a failed fetch still fails open.
  private initPromise?: Promise<void>;
  // The appName the init was claimed with. A later init() under a different
  // appName is refused (AM-4913, see init()); platform and defaults are not
  // compared, so a same-appName caller still rides along on the first run.
  private initAppName?: string;

  private default: { [platform: string]: AppSettings } = {
    android: {
      minAppVersion: '90',
    },
    ios: {
      minAppVersion: '2.29.0',
    },
    web: {
      minAppVersion: '1.0.0',
    },
  };

  /**
   * Returns the current settings value
   */
  public get current(): SharedAppSettings | undefined {
    return this._current;
  }

  constructor() {
    const appSettingsServiceEndpoint = this.appSettingsServiceEndpoint;

    this.settings = new ReplaySubject<SharedAppSettings>();
    this.$current = this.settings.asObservable();
    this.apiEndPoint = appSettingsServiceEndpoint;
  }

  public init(appName: string, platform: string, defaultSettings?: Partial<SharedAppSettings>): Promise<void> {
    // Idempotent AND concurrency-safe: every caller awaits the same in-flight run,
    // so `current` is guaranteed populated once any init() resolves. The first
    // caller's appName/platform/defaults win; later callers with the SAME appName ride along.
    if (this.initPromise === undefined) {
      this.initAppName = appName;
      this.initPromise = this.runInit(appName, platform, defaultSettings);
      return this.initPromise;
    }
    // A later caller with a DIFFERENT appName would silently get the first caller's
    // settings row. That is how web-traveler ran on the landing row (AM-4913), so it is
    // refused loudly: thrown outside production, logged in production (where the app
    // must keep booting), and in both cases the first claim stands. The throw is
    // deliberately synchronous, not a rejected promise: the booking stack calls
    // init() fire-and-forget with `.catch(() => undefined)`, which would swallow a
    // rejection and make the guard silent. Thrown, it fails bootstrap visibly.
    if (appName !== this.initAppName) {
      const message =
        `SharedAppSettingsService.init('${appName}') refused: already initialised as ` +
        `'${this.initAppName}'. Only one app may initialise app-settings (AM-4913).`;
      void this.log.error(message);
      if (!Config.isProduction) {
        throw new Error(message);
      }
    }
    return this.initPromise;
  }

  private async runInit(
    appName: string,
    platform: string,
    defaultSettings?: Partial<SharedAppSettings>,
  ): Promise<void> {
    const appSettings: AppSettings = await this.fetchRemoteSettings(appName, platform);
    this._current = this.createSharedAppSettings(appSettings, defaultSettings);
    this.settings.next(this._current);
  }

  public updateSettings(appSettings: AppSettings): void {
    this._current = this.createSharedAppSettings(appSettings, this._current);
    this.settings.next(this._current);
  }

  private async fetchRemoteSettings(appName: string, platform: string): Promise<AppSettings> {
    let appSettings = this.default[platform];

    try {
      const url = `${this.apiEndPoint}/settings?appName=${appName}&platform=${platform}`;
      appSettings = await this.http.get<AppSettings>(url);
    } catch (err) {
      // Always log, through the logging service so it reaches Datadog — this failure
      // silently degrades the whole session (defaults-only settings) and used to be
      // invisible in production, which made incidents like AM-4589 unattributable.
      void this.log.error('Failed to load app settings from server', err);
    }

    return appSettings;
  }

  private createSharedAppSettings(
    appSettings: AppSettings,
    defaultSettings: Partial<SharedAppSettings> = {},
  ): SharedAppSettings {
    // Start with default settings
    const settings = cloneDeep(defaultSettings);

    // Override the default settings in AppConfig with app settings retrieved from the server
    AppConfig.override(appSettings);

    // Loop through all AppConfig fields and add them to the settings object
    for (const key of Object.keys(AppConfig)) {
      const value = AppConfig[key];
      if (typeof value !== 'function') {
        settings[key] = value;
      }
    }

    // Apply per-session URL overrides last, so precedence is URL override → server row → app default
    // (AM-4658). It has to sit here rather than at the call site: this method runs on both `init()` and
    // `updateSettings()`, so a settings refresh re-applies the override instead of silently dropping it.
    //
    // Guarded because this runs inside the awaited `provideAppInitializer`: a throw here escapes into
    // Angular bootstrap and the app never renders. A diagnostic overlay must not be able to do that —
    // and it nearly did, via a param name that resolved through `Object.prototype`. Degrade to
    // un-overridden settings and log, rather than take the app down.
    try {
      this.urlOverrides.apply(settings as SharedAppSettings);
    } catch (err) {
      void this.log.error('Failed to apply URL setting overrides', err);
    }

    return settings as SharedAppSettings;
  }
}
